import CalculateIcon from "@mui/icons-material/Calculate";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import { Box, Divider, Stack, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { PanelHeader } from "../../components/UIToolkit/molecules/PanelHeader";
import { SystemReactTinyPopover, SystemReactTinyPopoverRootStyle } from "../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FONT_SIZE, LETTER_SPACING, RADIUS, SHADOW } from "../../theme/tokens";
import type { PBJFacilityInfo, PbjModelUpsertBody } from "../../utils/models";
import { useSearchPbjFacilities } from "../PbjCalculator/hooks";
import { PeriodSelector } from "./PeriodSelector";

type Range = { start: DateTime; end: DateTime };

type Props = {
	// The affordance the popover anchors to (the hero pill's "Change period" / a header button).
	children: ReactElement;
	range: Range;
	onRangeChange: (next: Range) => void;
	configDraft: PbjModelUpsertBody;
	onConfigDraftChange: (next: PbjModelUpsertBody) => void;
	// Persist + recompute the committed window (the page's Calculate). Closes the popover on click.
	onCalculate: () => void;
	isCalculating: boolean;
	isDirty: boolean;
};

/**
 * The period / facility controls, collapsed into a popover behind a "Change period"
 * affordance. Replaces the always-visible form-y top bar on the redesigned PBJ Model —
 * the page reads as a dashboard, not a data-entry screen, while every control (the FY-quarter
 * presets, the US-pinned start/end DatePickers, Facility ID, State Code, and Calculate) is
 * preserved verbatim inside the popover. Anchored to the caller's trigger via the app-standard
 * `SystemReactTinyPopover`. Owns only its own open state; all data lives at the page.
 */
// The period controls host portaled MUI surfaces — the Quick-pick Select menu and the two
// DatePicker poppers all render outside the popover's DOM subtree (to document.body). Two
// distinct ways a click on those must NOT close the popover:
//   1. The click lands on the portal surface itself (a menu item, calendar day, backdrop) — its
//      target sits inside one of these roots.
//   2. MUI's Select opens its menu on *mousedown* (not click) and scroll-locks the body; the
//      trailing `click` event — which react-tiny-popover binds its outside-detection to — then
//      reports `<body>`/`<html>` as its target, outside both the popover and the portal. So
//      opening the Select reads as an "outside click" and slams the popover shut. Guard against
//      it by ignoring a body/html-targeted click while a MUI overlay is mounted (i.e. open).
const PORTAL_SELECTOR = ".MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiPickersPopper-root, .MuiPopper-root";
const isPortalInteraction = (target: EventTarget | null): boolean => {
	if (target instanceof Element && target.closest(PORTAL_SELECTOR) !== null) return true;
	const isBodyOrHtml = target === document.body || target === document.documentElement;
	return isBodyOrHtml && document.querySelector(PORTAL_SELECTOR) !== null;
};

/** "Name — City, ST — CCN" for a searched facility; a nameless result degrades to its bare CCN. */
const facilityLabel = (f: PBJFacilityInfo): string => (f.provname ? `${f.provname} — ${f.city}, ${f.state} — ${f.provnum}` : f.provnum);

export const PeriodControlsPopover: FC<Props> = ({ children, range, onRangeChange, configDraft, onConfigDraftChange, onCalculate, isCalculating, isDirty }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const [open, setOpen] = useState(false);

	// Facility CCN search (the CASPER turnover linkage). Input-driven: the typed text feeds
	// useSearchPbjFacilities and picking a match writes the resolved CCN (provnum) into the draft.
	// `pickedFacility` only exists to render the chosen facility's name — the CCN in the draft is
	// the persisted truth, so a draft CCN we haven't re-resolved to a name still shows as the raw CCN.
	const [facilityInput, setFacilityInput] = useState("");
	const [pickedFacility, setPickedFacility] = useState<PBJFacilityInfo | null>(null);
	const { data: facilityOptions = [], isLoading: facilityLoading } = useSearchPbjFacilities(facilityInput);

	// Switching departments must drop the picked-facility display + typed query, so the picker
	// reflects the newly-active dept's persisted CCN rather than the prior dept's selection.
	// biome-ignore lint/correctness/useExhaustiveDependencies: departmentId IS the reset trigger
	useEffect(() => {
		setPickedFacility(null);
		setFacilityInput("");
	}, [configDraft.departmentId]);

	const ccnOptions = useMemo<SystemAutoCompleteOption<string>[]>(
		() => facilityOptions.map((f) => ({ label: facilityLabel(f), value: f.provnum })),
		[facilityOptions],
	);

	// Shown in the picker: the facility just chosen, else a label synthesized from the persisted
	// CCN so the current linkage is visible on load without re-fetching the facility's name.
	const ccnValue = useMemo<SystemAutoCompleteOption<string> | null>(() => {
		if (pickedFacility) return { label: facilityLabel(pickedFacility), value: pickedFacility.provnum };
		if (!configDraft.ccn) return null;
		return { label: configDraft.ccn, value: configDraft.ccn };
	}, [pickedFacility, configDraft.ccn]);

	const toggleOpen = () => {
		setOpen((prev) => {
			if (!prev) track("pbj_model_change_period_opened", { department_id: configDraft.departmentId });
			return !prev;
		});
	};

	const handleCalculate = () => {
		onCalculate();
		setOpen(false);
	};

	return (
		<SystemReactTinyPopover
			isOpen={open}
			onClose={() => setOpen(false)}
			onClickOutside={(e) => {
				if (!isPortalInteraction(e.target)) setOpen(false);
			}}
			positions={["bottom", "top"]}
			align="end"
			padding={8}
			data-testid="pbj-model-period-popover"
			contentRootStyle={{
				...SystemReactTinyPopoverRootStyle,
				borderRadius: RADIUS.POPOVER,
				boxShadow: SHADOW.POPOVER_FLOAT,
				padding: 0,
				maxWidth: 420,
			}}
			content={
				<Stack sx={{ minWidth: { xs: 280, sm: 372 }, p: 2.5, gap: 2 }}>
					<PanelHeader icon={<DateRangeOutlinedIcon />} title={t("pbj_model.hero.period_dialog.title")} />

					<PeriodSelector start={range.start} end={range.end} onChange={onRangeChange} disabled={isCalculating} />

					{/* Facility identity — a distinct, quieter concern from the reporting-period controls above. */}
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
						<Divider textAlign="left" sx={{ "&::before": { width: 0 }, "& .MuiDivider-wrapper": { pl: 0, pr: 1.5 } }}>
							<Typography
								component="span"
								sx={{
									fontSize: FONT_SIZE.LABEL_XS,
									fontWeight: 600,
									letterSpacing: LETTER_SPACING.LABEL,
									textTransform: "uppercase",
									color: "text.secondary",
								}}
							>
								{t("pbj_model.period.facility_section")}
							</Typography>
						</Divider>
						{/* CMS CCN — links this dept to its CASPER record, which is what lets turnover
						    auto-populate from CMS instead of being entered by hand. */}
						<SystemAutoComplete<string>
							dataTestId="pbj-model-ccn"
							fullWidth
							options={ccnOptions}
							value={ccnValue}
							onChange={(_e, option) => onConfigDraftChange({ ...configDraft, ccn: option?.value ?? null })}
							inputValue={facilityInput}
							onInputChange={(_e, value) => setFacilityInput(value)}
							loading={facilityLoading}
							textFieldProps={{
								label: t("pbj_model.config.ccn"),
								defaultHelperText: t("pbj_model.config.ccn_help"),
								reserveHeightForHelperText: false,
							}}
						/>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 2fr) minmax(0, 1fr)" }, columnGap: 1.25, rowGap: 1 }}>
							<SystemValidationTextField
								dataTestId="pbj-model-facility-id"
								label={t("pbj_model.config.facility_id")}
								value={configDraft.facilityId ?? ""}
								onChange={(e) => onConfigDraftChange({ ...configDraft, facilityId: e.target.value || null })}
								reserveHeightForHelperText={false}
								inputProps={{ inputProps: { maxLength: 32 } }}
							/>
							<SystemValidationTextField
								dataTestId="pbj-model-state-code"
								label={t("pbj_model.config.state_code")}
								value={configDraft.stateCode ?? ""}
								onChange={(e) => onConfigDraftChange({ ...configDraft, stateCode: e.target.value || null })}
								reserveHeightForHelperText={false}
								inputProps={{ inputProps: { maxLength: 2 } }}
							/>
						</Box>
					</Box>

					<Stack direction="row" sx={{ justifyContent: "flex-end", mt: 0.5 }}>
						<SystemButton
							buttonVariant={isDirty ? "primary" : "secondary"}
							onClick={handleCalculate}
							loading={isCalculating}
							leadingIcon={<CalculateIcon sx={{ fontSize: 18 }} />}
							data-testid="pbj-model-calculate"
							noFullWidthMobile
						>
							{t("pbj_model.calculate")}
						</SystemButton>
					</Stack>
				</Stack>
			}
		>
			<Box onClick={toggleOpen} data-testid="pbj-model-change-period-trigger" sx={{ display: "inline-flex", cursor: "pointer" }}>
				{children}
			</Box>
		</SystemReactTinyPopover>
	);
};
